import {
  doc, getDoc, setDoc, onSnapshot, Unsubscribe
} from 'firebase/firestore';
import { db, auth } from '../config/firebase';

/**
 * Permission keys that can be toggled per role
 */
export type PermissionKey =
  | 'patient_data'
  | 'session_notes'
  | 'billing'
  | 'scheduling'
  | 'hr_all'
  | 'analytics'
  | 'assign_roles'
  | 'create_users'
  | 'messages'
  | 'forms_measures'
  | 'activity_log'
  | 'prescriptions'
  | 'team_chat'
  | 'staff_meetings';

export const PERMISSION_LABELS: Record<PermissionKey, string> = {
  patient_data: 'Patient Data',
  session_notes: 'Session / Clinical Notes',
  billing: 'Billing & Invoices',
  scheduling: 'Appointments & Schedule',
  hr_all: 'HR Documents (All Staff)',
  analytics: 'Analytics & Reports',
  assign_roles: 'Assign Roles',
  create_users: 'Create / Invite Users',
  messages: 'Client Messages',
  forms_measures: 'Forms & Measures',
  activity_log: 'Activity Log',
  prescriptions: 'Prescriptions',
  team_chat: 'Team Chat',
  staff_meetings: 'Staff Meetings',
};

export const ALL_PERMISSIONS: PermissionKey[] = Object.keys(PERMISSION_LABELS) as PermissionKey[];

/**
 * A practice role with its permissions
 */
export interface PracticeRole {
  id: string;
  name: string;
  description: string;
  isDefault: boolean; // true = one of the 4 built-in roles (cannot delete/rename)
  color: string;
  permissions: Record<PermissionKey, boolean>;
}

/**
 * Practice roles document stored in Firestore
 */
export interface PracticeRolesDoc {
  roles: PracticeRole[];
  updatedAt: string;
}

// Note: "Own HR Documents" is ALWAYS granted regardless — not in the permission list

const DEFAULT_ROLES: PracticeRole[] = [
  {
    id: 'system_admin',
    name: 'System Administrator',
    description: 'Full system access, user & role management',
    isDefault: true,
    color: '#EF4444',
    permissions: {
      patient_data: true,
      session_notes: true,
      billing: true,
      scheduling: true,
      hr_all: true,
      analytics: true,
      assign_roles: true,
      create_users: true,
      messages: true,
      forms_measures: true,
      activity_log: true,
      prescriptions: true,
      team_chat: true,
      staff_meetings: true,
    },
  },
  {
    id: 'clinical_director',
    name: 'Clinical Director',
    description: 'Clinical oversight, provider supervision, all patient data',
    isDefault: true,
    color: '#D4A017',
    permissions: {
      patient_data: true,
      session_notes: true,
      billing: true,
      scheduling: true,
      hr_all: true,
      analytics: true,
      assign_roles: true,
      create_users: true,
      messages: true,
      forms_measures: true,
      activity_log: true,
      prescriptions: true,
      team_chat: true,
      staff_meetings: true,
    },
  },
  {
    id: 'provider',
    name: 'Provider (Clinician)',
    description: 'Direct patient care, own caseload & notes',
    isDefault: true,
    color: '#10B981',
    permissions: {
      patient_data: true,
      session_notes: true,
      billing: false,
      scheduling: true,
      hr_all: false,
      analytics: true,
      assign_roles: false,
      create_users: false,
      messages: true,
      forms_measures: true,
      activity_log: false,
      prescriptions: true,
      team_chat: true,
      staff_meetings: true,
    },
  },
  {
    id: 'administrative_staff',
    name: 'Administrative Staff',
    description: 'Scheduling, billing, office operations',
    isDefault: true,
    color: '#3B82F6',
    permissions: {
      patient_data: false,
      session_notes: false,
      billing: true,
      scheduling: true,
      hr_all: false,
      analytics: true,
      assign_roles: false,
      create_users: true,
      messages: true,
      forms_measures: false,
      activity_log: false,
      prescriptions: false,
      team_chat: true,
      staff_meetings: true,
    },
  },
  {
    // hr_admin already exists in UserRole and is honored by the dashboard
    // route guard (App.tsx:82) and the staff sign-in check, and SettingsPage
    // already describes it — it was simply missing from the role list, so it
    // could never be assigned to anyone.
    id: 'hr_admin',
    name: 'HR Admin',
    description: 'HR documents, employee compliance, onboarding',
    isDefault: true,
    color: '#8B5CF6',
    permissions: {
      patient_data: false,
      session_notes: false,
      billing: false,
      scheduling: false,
      hr_all: true,
      analytics: false,
      assign_roles: false,
      create_users: false,
      messages: true,
      forms_measures: false,
      activity_log: false,
      prescriptions: false,
      team_chat: true,
      staff_meetings: true,
    },
  },
];

const COLLECTION = 'practice_roles';

class PracticeRolesService {
  private static instance: PracticeRolesService;
  private listeners: (() => void)[] = [];
  private cachedRoles: PracticeRole[] = [...DEFAULT_ROLES];
  private firestoreUnsubscribe: Unsubscribe | null = null;
  private currentOwnerId: string | null = null;
  private initialized = false;

  static getInstance(): PracticeRolesService {
    if (!PracticeRolesService.instance) {
      PracticeRolesService.instance = new PracticeRolesService();
    }
    return PracticeRolesService.instance;
  }

  /**
   * Initialize real-time listener for practice roles
   */
  initialize(practiceOwnerId: string): void {
    if (this.currentOwnerId === practiceOwnerId && this.initialized) return;

    this.cleanup();
    this.currentOwnerId = practiceOwnerId;

    const docRef = doc(db, COLLECTION, practiceOwnerId);
    this.firestoreUnsubscribe = onSnapshot(docRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data() as PracticeRolesDoc;
        this.cachedRoles = data.roles;
      } else {
        // No custom roles yet — use defaults
        this.cachedRoles = [...DEFAULT_ROLES];
      }
      this.initialized = true;
      this.notifyListeners();
    }, (error) => {
      console.error('[PracticeRoles] Listener error:', error);
      this.cachedRoles = [...DEFAULT_ROLES];
      this.initialized = true;
      this.notifyListeners();
    });
  }

  /**
   * Get all roles for the current practice
   */
  getRoles(): PracticeRole[] {
    return this.cachedRoles;
  }

  /**
   * Get a specific role by ID
   */
  getRole(roleId: string): PracticeRole | undefined {
    return this.cachedRoles.find(r => r.id === roleId);
  }

  /**
   * Check if a role has a specific permission
   */
  hasPermission(roleId: string, permission: PermissionKey): boolean {
    const role = this.getRole(roleId);
    if (!role) return false;
    return role.permissions[permission] === true;
  }

  /**
   * Check if a role ID string matches any role (supports custom role IDs)
   */
  roleExists(roleId: string): boolean {
    return this.cachedRoles.some(r => r.id === roleId);
  }

  /**
   * Save roles to Firestore
   */
  async saveRoles(roles: PracticeRole[]): Promise<void> {
    if (!this.currentOwnerId) throw new Error('Not initialized');

    const docRef = doc(db, COLLECTION, this.currentOwnerId);
    await setDoc(docRef, {
      roles,
      updatedAt: new Date().toISOString(),
    });
  }

  /**
   * Update a single role's permissions
   */
  async updateRolePermissions(roleId: string, permissions: Record<PermissionKey, boolean>): Promise<void> {
    const updated = this.cachedRoles.map(r =>
      r.id === roleId ? { ...r, permissions } : r
    );
    await this.saveRoles(updated);
  }

  /**
   * Add a new custom role
   */
  async addRole(role: Omit<PracticeRole, 'isDefault'>): Promise<void> {
    const newRole: PracticeRole = { ...role, isDefault: false };
    await this.saveRoles([...this.cachedRoles, newRole]);
  }

  /**
   * Delete a custom role (cannot delete default roles)
   */
  async deleteRole(roleId: string): Promise<void> {
    const role = this.getRole(roleId);
    if (!role) throw new Error('Role not found');
    if (role.isDefault) throw new Error('Cannot delete default roles');

    await this.saveRoles(this.cachedRoles.filter(r => r.id !== roleId));
  }

  /**
   * Update a custom role's name/description
   */
  async updateRoleMeta(roleId: string, name: string, description: string): Promise<void> {
    const role = this.getRole(roleId);
    if (!role) throw new Error('Role not found');
    if (role.isDefault) throw new Error('Cannot rename default roles');

    const updated = this.cachedRoles.map(r =>
      r.id === roleId ? { ...r, name, description } : r
    );
    await this.saveRoles(updated);
  }

  /**
   * Subscribe to role changes
   */
  subscribe(listener: () => void): void {
    this.listeners.push(listener);
  }

  unsubscribe(listener: () => void): void {
    this.listeners = this.listeners.filter(l => l !== listener);
  }

  private notifyListeners(): void {
    this.listeners.forEach(l => l());
  }

  private cleanup(): void {
    if (this.firestoreUnsubscribe) {
      this.firestoreUnsubscribe();
      this.firestoreUnsubscribe = null;
    }
    this.initialized = false;
    this.currentOwnerId = null;
  }

  /**
   * Get role names for dropdown (invite flow)
   */
  getRoleOptions(): { value: string; label: string }[] {
    return this.cachedRoles
      .filter(r => r.id !== 'client') // Never show client in staff role picker
      .map(r => ({ value: r.id, label: r.name }));
  }

  /**
   * Get default roles (for reset)
   */
  static getDefaults(): PracticeRole[] {
    return [...DEFAULT_ROLES];
  }
}

export default PracticeRolesService;
